import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Loader2, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "../ui/hover-card";
import { api, formatApiErrorDetail } from "../../lib/api";

// Simple one-click store: each booklet has its own Buy button. No tiers.
export default function HeroBookletStore() {
    const reduce = useReducedMotion();
    const [booklets, setBooklets] = useState([]);
    const [buyingSlug, setBuyingSlug] = useState(null);
    const [emailModal, setEmailModal] = useState(null); // { slug, title, price } | null
    const [emailValue, setEmailValue] = useState("");

    useEffect(() => {
        api.get("/booklets")
            .then((r) => setBooklets(r.data.booklets || []))
            .catch(() => setBooklets([]));
    }, []);

    function openBuy(b) {
        // Try to prefill from a previous purchase
        try {
            const cached = localStorage.getItem("sd_buyer_email");
            if (cached) setEmailValue(cached);
        } catch (_) { /* ignore */ }
        setEmailModal({ slug: b.slug, title: b.title, price: b.price ?? 9 });
    }

    async function confirmBuy() {
        const email = emailValue.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error("Please enter a valid email so we can deliver the booklet.");
            return;
        }
        const slug = emailModal.slug;
        setBuyingSlug(slug);
        try {
            try { localStorage.setItem("sd_buyer_email", email); } catch (_) { /* ignore */ }
            const { data } = await api.post("/booklets/checkout", {
                slug,
                email,
                origin_url: window.location.origin,
                currency: "NZD",
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    "Couldn't start checkout. Please try again.",
            );
            setBuyingSlug(null);
        }
    }

    return (
        <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 w-full max-w-3xl border-2 border-ink bg-white p-5 shadow-[6px_6px_0_0_#2D2D2D] lg:p-6"
            id="booklets"
            data-testid="hero-booklet-store"
        >
            {/* Header */}
            <div className="mb-5 flex items-center gap-3">
                <span className="grid h-8 w-8 place-items-center border-2 border-coral bg-coral text-white">
                    <BookOpen className="h-4 w-4" />
                </span>
                <div className="font-display text-base font-black uppercase tracking-tight text-ink lg:text-lg">
                    Simple Digital Booklets
                </div>
            </div>

            {/* Booklet grid — each is its own buy */}
            <div className="grid gap-2 sm:grid-cols-2" data-testid="booklet-list">
                {booklets.map((b) => (
                    <HoverCard key={b.slug} openDelay={120} closeDelay={100}>
                        <div
                            className="group relative flex w-full min-w-0 items-center gap-3 border-2 border-ink/15 bg-white px-3 py-2.5 text-left transition-all hover:border-coral"
                            data-testid={`booklet-item-${b.slug}`}
                        >
                            <HoverCardTrigger asChild>
                                <Link
                                    to={`/booklets/${b.slug}/read`}
                                    className="min-w-0 flex-1"
                                    data-testid={`booklet-title-${b.slug}`}
                                >
                                    <span className="block font-display text-sm font-black uppercase tracking-tight text-ink">
                                        {b.title}
                                    </span>
                                    <span className="mt-0.5 block truncate text-[11px] text-ink/60">
                                        {b.subtitle}
                                    </span>
                                </Link>
                            </HoverCardTrigger>

                            <button
                                type="button"
                                onClick={() => openBuy(b)}
                                disabled={buyingSlug === b.slug}
                                className="inline-flex shrink-0 items-center gap-1.5 border-2 border-coral bg-coral px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:bg-coral-hover disabled:opacity-60"
                                data-testid={`booklet-buy-${b.slug}`}
                            >
                                {buyingSlug === b.slug ? (
                                    <>
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        <ShoppingBag className="h-3 w-3" />
                                        ${(b.price ?? 9).toFixed(0)}
                                    </>
                                )}
                            </button>
                        </div>

                        <HoverCardContent
                            side="right"
                            align="start"
                            sideOffset={12}
                            className="w-80 border-2 border-ink bg-white p-0 shadow-[6px_6px_0_0_#2D2D2D]"
                            data-testid={`booklet-preview-${b.slug}`}
                        >
                            <BookletCover book={b} />
                        </HoverCardContent>
                    </HoverCard>
                ))}
                {booklets.length === 0 && (
                    <div className="col-span-full py-8 text-center text-sm uppercase tracking-widest text-ink/45">
                        No booklets yet.
                    </div>
                )}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest text-ink/55">
                <span>Instant PDF · Secure Stripe checkout · NZD + GST</span>
            </div>

            {/* Email prompt modal */}
            {emailModal && (
                <div
                    className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4"
                    onClick={() => (buyingSlug ? null : setEmailModal(null))}
                    data-testid="booklet-email-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md border-2 border-ink bg-white p-6 shadow-[8px_8px_0_0_#2D2D2D]"
                    >
                        <div className="mb-4 flex items-center justify-between">
                            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-coral">
                                Deliver to
                            </div>
                            <button
                                type="button"
                                onClick={() => setEmailModal(null)}
                                disabled={!!buyingSlug}
                                className="text-ink/50 hover:text-coral disabled:opacity-40"
                                aria-label="Close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="font-display text-xl font-black uppercase tracking-tight text-ink">
                            {emailModal.title}
                        </div>
                        <div className="mt-1 text-sm text-ink/65">
                            We'll email your download link the moment payment goes through.
                        </div>
                        <form
                            onSubmit={(e) => { e.preventDefault(); confirmBuy(); }}
                            className="mt-4"
                        >
                            <input
                                autoFocus
                                type="email"
                                required
                                value={emailValue}
                                onChange={(e) => setEmailValue(e.target.value)}
                                placeholder="you@yourbusiness.co.nz"
                                className="w-full border-2 border-ink/25 bg-white px-4 py-3 text-ink placeholder:text-ink/40 focus:border-coral focus:outline-none"
                                data-testid="booklet-email-input"
                            />
                            <button
                                type="submit"
                                disabled={!!buyingSlug}
                                className="mt-4 inline-flex w-full items-center justify-center gap-2 border-2 border-coral bg-coral px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
                                data-testid="booklet-email-continue"
                            >
                                {buyingSlug ? (
                                    <>Redirecting… <Loader2 className="h-4 w-4 animate-spin" /></>
                                ) : (
                                    <><ShoppingBag className="h-4 w-4" /> Continue · ${emailModal.price.toFixed(0)}</>
                                )}
                            </button>
                        </form>
                        <div className="mt-4 text-[10px] uppercase tracking-widest text-ink/45">
                            Secure Stripe checkout · No spam · NZD + GST
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
}

// A styled "cover peek" that mimics the printed booklet, shown on hover.
function BookletCover({ book }) {
    const preview = book.preview || {};
    const images = book.preview_images || [];
    const hasImages = images.length > 0;
    return (
        <div className="relative overflow-hidden bg-page">
            {/* Coral top-band matching the PDF */}
            <div className="h-2 w-full bg-coral" />
            <div className="p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-coral">
                    Simple Digital · Booklet
                </div>
                <div className="mt-2 font-display text-xl font-black leading-[1.05] tracking-tight text-ink">
                    {book.title}
                </div>
                <div className="mt-1.5 font-serif text-sm italic leading-snug text-ink/75">
                    {book.subtitle}
                </div>

                {hasImages && (
                    <div className="mt-3" data-testid={`booklet-preview-pages-${book.slug}`}>
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-coral">
                            Peek inside · first 2 pages
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {images.slice(0, 2).map((u, i) => (
                                <img
                                    key={i}
                                    src={`${process.env.REACT_APP_BACKEND_URL}${u}`}
                                    alt={`${book.title} — page ${i + 1} preview`}
                                    loading="lazy"
                                    className={`w-full border-2 border-ink/20 bg-white shadow-[3px_3px_0_0_#2D2D2D] ${
                                        i === 1 ? "rotate-1" : "-rotate-1"
                                    }`}
                                    data-testid={`booklet-preview-page-${book.slug}-${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {book.meta && book.meta.length > 0 && (
                    <div className="mt-3 border border-ink/20 bg-white/70">
                        {book.meta.map((m, i) => (
                            <div
                                key={m.label + i}
                                className={`flex items-center justify-between gap-3 px-2.5 py-1.5 text-[11px] ${
                                    i > 0 ? "border-t border-ink/10" : ""
                                }`}
                            >
                                <span className="font-bold uppercase tracking-wider text-ink/70">
                                    {m.label}
                                </span>
                                <span className="text-ink/80">{m.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {!hasImages && preview.heading && (
                    <>
                        <div className="mt-4 border-t border-dashed border-ink/20 pt-3">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral">
                                Inside · Page 1 preview
                            </div>
                            <div className="mt-1 font-display text-[13px] font-black leading-snug text-ink">
                                {preview.heading}
                            </div>
                            {preview.intro && (
                                <p className="mt-1.5 text-[11px] leading-snug text-ink/70">
                                    {preview.intro.length > 130
                                        ? preview.intro.slice(0, 130).trim() + "…"
                                        : preview.intro}
                                </p>
                            )}
                        </div>
                        {preview.points && preview.points.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {preview.points.slice(0, 3).map((pt, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-2 text-[11px] leading-snug text-ink/85"
                                    >
                                        <span className="mt-0.5 text-coral">▸</span>
                                        <span className="line-clamp-2">{pt}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}

                <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-ink/55">
                    <span>A full easy to read booklet, a lil every day!</span>
                    <span className="text-coral">${(book.price ?? 9).toFixed(0)} · GST</span>
                </div>

                <Link
                    to={`/booklets/${book.slug}/read`}
                    className="mt-3 inline-flex w-full items-center justify-center gap-1.5 border-2 border-ink bg-white px-3 py-2 font-display text-[11px] font-bold uppercase tracking-wider text-ink transition-all hover:-translate-y-0.5 hover:border-coral hover:text-coral"
                    data-testid={`booklet-sample-link-${book.slug}`}
                >
                    Read a free sample →
                </Link>
            </div>
        </div>
    );
}
